import { Component, Host, h, State, Prop } from "@stencil/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/nl";
import { sensorInfo } from "../../utils/sensorInfo";
import {
  ILiftStatusSensorDataResponse,
  TSeverity,
  IStatusMessage,
  ILiftStatusWebComponentError,
} from "../../utils/types";
import { getStore, ILiftStatusStore } from "../../store";
import statusCodesState from "../../stores/status-codes.store";
import { isEmptyObject } from "../../utils/is-empty-object";
import { getSafeRelativeTime } from "../../utils/safe-relative-time";
import { isNil } from "lodash-es";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";

@Component({
  tag: "ls-status-messages",
  styleUrl: "ls-status-messages.css",
  shadow: true,
})
export class LsStatusMessages {
  /**
   * Whether or not to show the title of the component.
   */
  @Prop() readonly hideTitle = false;

  /**
   * Allows user to filter messages based on severity
   */
  // Leading comma is to allow for <empty_string>
  @Prop() readonly severityOptions = ",impact,informational,non-impact";

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Used to store severity options after parsing
   */
  private severityFilter: TSeverity[];

  /**
   * The data received from the subscription is placed in this state.
   * The component is automatically rerendered after the state changes.
   */
  @State() statusMessages: IStatusMessage[] = [];

  /**
   * The timestamp of when the sensor last changed.
   */
  @State() lastChange = "";

  /**
   * The timer is used to update the relative timestamps.
   */
  @State() time: number = Date.now();

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: "",
  };

  private store: ILiftStatusStore | null = null;

  /**
   * The sensor ID associated with the status messages.
   */
  private readonly sensorId = 513;

  private get componentTitle(): string {
    return sensorInfo.getLabel(this.sensorId, this.store?.state.locale);
  }

  /**
   * Keeps track of the setInterval ID.
   */
  private timer: number;
  private isComponentMounted = false;
  private storeUnsubscribe: (() => void) | null = null;

  /**
   * Updates the error state based on current component conditions
   */
  private updateErrorState() {
    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };
      this.stopTimer();
      return;
    }

    // No error detected
    this.errorState = {
      hasError: false,
      messageKey: undefined,
      type: "",
    };
    this.startTimer();
  }

  // This function processes the raw data from the socket server
  private updateData = (sensorData: ILiftStatusSensorDataResponse) => {
    this.updateErrorState();

    // Only listen to our own sensor identifier
    if (isEmptyObject(sensorData?.values?.[this.sensorId])) return;

    const items = sensorData.values[this.sensorId]
      ?.split(",")
      .filter((id) => id.trim() !== "");

    if (isNil(items) || items.length === 0) {
      this.lastChange = getSafeRelativeTime(
        sensorData.updated[this.sensorId],
        this.store?.state.locale
      );
      this.statusMessages = [];
      return;
    }

    const result: IStatusMessage[] = [];
    for (const item of items) {
      const parts = item.split(":");
      const identifier = parts[0];
      const timestampValue = parseInt(parts[1], 10) / 1000;
      const timestamp = dayjs.unix(timestampValue).locale(this.store?.state.locale);
      const humanReadableTimestamp = timestamp.format("D MMM YYYY H:mm:ss");
      const relativeTimestamp = getSafeRelativeTime(
        timestampValue * 1000,
        this.store?.state.locale
      ); // Convert back to milliseconds
      const active = parseInt(parts[2], 10);

      // The description and severity level are retrieved from the central store
      const description =
        statusCodesState[identifier]?.description ?? identifier;
      const severity = statusCodesState[identifier]?.level ?? null;

      // Add the item to the results array
      if (active === 1 && this.severityFilter?.includes(severity)) {
        result.push({
          id: identifier,
          humanReadableTimestamp,
          relativeTimestamp,
          description,
          severity,
        });
      }
    }

    this.lastChange = getSafeRelativeTime(
      sensorData.updated[this.sensorId],
      this.store?.state.locale
    );
    this.statusMessages = result;
  };

  private isSeverity = (value: string): value is TSeverity => {
    return ["", "impact", "informational", "non-impact"].includes(
      value as TSeverity,
    );
  };

  connectedCallback() {
    this.startTimer();

    const severityFilterSplit = this.severityOptions
      .replace(/\s+/g, "")
      .split(",")
      .filter((id) => id.trim() !== "");
    const severityFilter: TSeverity[] = [];

    severityFilterSplit.forEach((item) => {
      if (this.isSeverity(item) && !severityFilter.includes(item)) {
        severityFilter.push(item);
      }
    });

    if (severityFilter.length > 0) {
      this.severityFilter = severityFilter;
    } else {
      console.group(
        "%cError%c [LsStatusMessages] Incorrect severity filter options supplied",
        "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
        "",
      );
      console.log(
        "Allowed options: '<empty_string>, impact, informational and non-impact",
      );
      console.log(
        "Check https://springtree.github.io/ednl-liftstatus-web-components/#/components/ls-status-messages for more information",
      );
      console.groupEnd();
    }
  }

  private startTimer() {
    // Clear any existing timer first
    this.stopTimer();

    // The timer is used to update the relative timestamps
    this.timer = window.setInterval(() => {
      // Check if component is still mounted
      if (!this.isComponentMounted) {
        this.stopTimer();
        return;
      }

      // Access data only if needed to minimize closure scope
      const sensorData = this.store?.state?.currentSensorData;
      if (sensorData !== undefined) {
        // Update the data, so that the relative timestamp gets updated
        this.updateData(sensorData);
        // The timestamp is actually not displayed, but is only used to update the state and rerender the DOM
        this.time = Date.now();
      }
    }, 20000);
  }

  private stopTimer() {
    clearInterval(this.timer);
    this.timer = null;
  }

  private getSeverityLabel(severity?: TSeverity | null): string {
    const key =
      severity !== undefined && severity !== null && severity !== ""
        ? `ui:impact_level.${severity.replace(/-/g, "_")}`
        : "ui:impact_level.unknown";
    return this.store.t(key);
  }

  async componentWillLoad() {
    dayjs.extend(relativeTime);

    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store, () => {
      this.updateData(this.store.state.currentSensorData);
    }, { skipForceUpdate: true });

    this.isComponentMounted = true;
    this.updateData(this.store.state.currentSensorData);

    const sensorDataUnsubscribe = this.store.onChange(
      "currentSensorData",
      (sensorData) => {
        this.updateData(sensorData);
      },
    );

    const errorUnsubscribe = this.store.onChange("error", () => {
      this.updateErrorState();
    });

    this.storeUnsubscribe = () => {
      sensorDataUnsubscribe();
      errorUnsubscribe();
    };

  }

  render() {
    return (
      <Host>
        <slot></slot>
        <div class="ls-status-messages">
          {!this.hideTitle && (
            <div class="title">
              {this.componentTitle || this.store.t("ui:status_messages.title")}
            </div>
          )}

          {/* If lastChange does not have a timestamp, we know the data is still loading */}
          {this.lastChange === "" && !this.errorState.hasError ? (
            <div class="messages">
              <div class="label">
                {this.store.t("ui:status_messages.loading")}
              </div>
            </div>
          ) : (
            <div class="messages">
              {this.errorState.hasError && (
                <div class="subtitle">
                  {this.store.t("ui:status_messages.last_known")}
                </div>
              )}

              {/* Check if there are any status messages */}
              {this.statusMessages.length > 0 ? (
                this.statusMessages.map((message) => (
                  <div
                    class={`message ${statusCodesState[message.id]?.description.length > 0 ? "has-status-code" : ""}`}
                  >
                    <div
                      class="label"
                      title={statusCodesState[message.id]?.description.length > 0 ? undefined : message.id}
                    >
                      {message.description || message.id}
                    </div>
                    {statusCodesState[message.id]?.description.length > 0 && (
                      <div class="status-code">{message.id}</div>
                    )}
                    <div class={`severity ${message.severity}`}>
                      {this.getSeverityLabel(message.severity)}
                    </div>
                    <div class="human-readable-timestamp">
                      {message.humanReadableTimestamp}
                    </div>
                    <div class="timestamp relative-timestamp">
                      {message.relativeTimestamp}
                    </div>
                  </div>
                ))
              ) : (
                <dt class="label">
                  {this.store.t("ui:status_messages.none")}
                </dt>
              )}

              {/* Hide the last changed text when there are status messages */}
              {this.statusMessages.length === 0 && (
                <div class="timestamp">{this.lastChange}</div>
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }

  disconnectedCallback() {
    this.isComponentMounted = false;
    this.stopTimer();

    // Unsubscribe from store changes
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }

  }
}
